'use client';

import { useMemo } from 'react';
import { Box, token } from '@terra/ui-styled-system';
import { Badge, BodyDefault } from '@terra/ui-design-system';
import {
  Activity01Icon,
  Alert01Icon,
  CrownIcon,
  Key01Icon,
  RadioIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useTargetId } from '../../../store/projectStore';
import { TARGET_TYPE } from '../../../utils/target-scope.utils';
import { useInternalNetworkStats } from '../../../hooks/api/useInternalNetworkStats';
import { useInternalNetworkSignalsTrend } from '../../../hooks/api/useInternalNetworkOverview';
import { useHomeVisitBaseline } from '../../../hooks/dashboard/useHomeVisitBaseline';
import { useThreadsWaitingForInput } from '../../../hooks/dashboard/useThreadsWaitingForInput';
import { usePersistTargetVisit } from '../../../hooks/usePersistTargetVisit';
import { FindingsSeverityPostureCard } from '../../dashboard/FindingsSeverityPostureCard';
import { HomePageLayout } from '../../dashboard/HomePageLayout';
import { HomeWorkGrid } from '../../dashboard/HomeWorkGrid';
import { buildNeedsAttentionItems } from '../../dashboard/needs-attention-items';
import { fromSeverityBuckets } from '../../dashboard/posture-items';
import {
  SnapshotSinceLastVisitStrip,
  type SnapshotStripCell,
} from '../../dashboard/SnapshotSinceLastVisitStrip';
import type { LocalHealthIssue } from '../../dashboard/TargetHealthCard';
import { WelcomeHeader } from '../../dashboard/WelcomeHeader';
import { InternalNetworkReferenceLists } from './InternalNetworkReferenceLists';
import { SignalsTrendWidget } from './SignalsTrendWidget';

const MUTED_ICON_COLOR = token('colors.fg.neutral.muted');

const ACTIVE_DISCOVERY_STATUSES = new Set([
  'ENROLLING',
  'COLLECTING',
  'IMPORTING',
  'NORMALIZING',
  'ANALYZING',
]);

export function InternalNetworkOverviewTab() {
  const t = useTranslations('home');
  const networkId = useTargetId();
  const targetId = networkId ?? null;
  const targetType = TARGET_TYPE.INTERNAL_NETWORK;
  const {
    baselineAt,
    isReady: isBaselineReady,
    previousSnapshot: previousMetrics,
  } = useHomeVisitBaseline({ targetId, targetType });
  const { stats, isLoading: isStatsLoading } =
    useInternalNetworkStats(targetId);
  const { trend, isLoading: isTrendLoading } = useInternalNetworkSignalsTrend({
    networkId: targetId,
  });
  const threadsWaitingForInput = useThreadsWaitingForInput(networkId ?? '');
  const severityCounts = useMemo(
    () =>
      stats ? fromSeverityBuckets(stats.findingsCountsBySeverity) : undefined,
    [stats],
  );

  const signalsTotal = stats?.signalCount ?? 0;
  const openFindingCount = stats?.openFindingCount ?? 0;
  const crownJewelCount = stats?.crownJewelCount ?? 0;
  const ownedPrincipalCount = stats?.ownedPrincipalCount ?? 0;
  const onlineEnrollmentCount = stats?.onlineEnrollmentCount ?? 0;

  const visitSnapshot = useMemo(() => {
    if (!networkId || !stats) {
      return null;
    }

    return {
      signalsCount: signalsTotal,
      findingsCount: openFindingCount,
      crownJewelCount,
      ownedPrincipalCount,
      onlineEnrollmentCount,
    };
  }, [
    networkId,
    stats,
    signalsTotal,
    openFindingCount,
    crownJewelCount,
    ownedPrincipalCount,
    onlineEnrollmentCount,
  ]);

  usePersistTargetVisit({ targetId, targetType, snapshot: visitSnapshot });

  if (!networkId) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BodyDefault color="fg.neutral.muted">
          {t('selectInternalNetwork')}
        </BodyDefault>
      </Box>
    );
  }

  const attentionItems = buildNeedsAttentionItems({
    counts: {
      discoveryFailed: stats?.latestDiscoveryStatus === 'FAILED' ? 1 : 0,
      waitingForInput: threadsWaitingForInput.length,
      criticalFindings: severityCounts?.Critical ?? 0,
    },
    waitingForInputThreadId: threadsWaitingForInput[0] ?? null,
  });
  const localHealthIssues: LocalHealthIssue[] =
    stats && onlineEnrollmentCount === 0
      ? [
          {
            key: 'noSensorsOnline',
            title: t('health.noSensorsOnline.title'),
            description: t('health.noSensorsOnline.description'),
            destination: { kind: 'settings', section: 'general' },
          },
        ]
      : [];
  const isDiscoveryRunning = stats?.latestDiscoveryStatus
    ? ACTIVE_DISCOVERY_STATUSES.has(stats.latestDiscoveryStatus)
    : false;

  const stripCells: SnapshotStripCell[] = [
    {
      key: 'signals',
      label: t('sinceLastVisit.signals'),
      icon: <Activity01Icon size={16} color={MUTED_ICON_COLOR} />,
      current: signalsTotal,
      previous: previousMetrics?.signalsCount,
      destination: { kind: 'list', destination: { list: 'signals' } },
    },
    {
      key: 'findings',
      label: t('sinceLastVisit.openFindings'),
      icon: <Alert01Icon size={16} color={MUTED_ICON_COLOR} />,
      current: openFindingCount,
      previous: previousMetrics?.findingsCount,
      destination: { kind: 'list', destination: { list: 'findings' } },
    },
    {
      key: 'crownJewels',
      label: t('sinceLastVisit.crownJewels'),
      icon: <CrownIcon size={16} color={MUTED_ICON_COLOR} />,
      current: crownJewelCount,
      previous: previousMetrics?.crownJewelCount,
    },
    {
      key: 'ownedPrincipals',
      label: t('sinceLastVisit.ownedPrincipals'),
      icon: <Key01Icon size={16} color={MUTED_ICON_COLOR} />,
      current: ownedPrincipalCount,
      previous: previousMetrics?.ownedPrincipalCount,
    },
    {
      key: 'onlineSensors',
      label: t('sinceLastVisit.onlineSensors'),
      icon: <RadioIcon size={16} color={MUTED_ICON_COLOR} />,
      current: onlineEnrollmentCount,
      previous: previousMetrics?.onlineEnrollmentCount,
    },
  ];

  return (
    <HomePageLayout>
      <WelcomeHeader
        lastVisitAt={isBaselineReady ? baselineAt : undefined}
        status={
          isDiscoveryRunning ? (
            <Badge variant="outline" size="sm" colorPalette="blue">
              {t('networkActivity.discoveryRunning')}
            </Badge>
          ) : null
        }
      />
      <SnapshotSinceLastVisitStrip
        applicationId={networkId}
        cells={stripCells}
        isLoading={isStatsLoading || !isBaselineReady}
      />
      <HomeWorkGrid
        applicationId={networkId}
        attentionItems={attentionItems}
        isAttentionLoading={isStatsLoading}
        localHealthIssues={localHealthIssues}
        healthyDetail={
          stats
            ? t('health.sensorsOnline', { count: onlineEnrollmentCount })
            : undefined
        }
      />
      <FindingsSeverityPostureCard
        applicationId={networkId}
        counts={severityCounts}
        isLoading={isStatsLoading}
      />
      <SignalsTrendWidget
        title={t('networkActivity.signalsTrend')}
        dailyCounts={trend?.dailyData ?? []}
        isLoading={isTrendLoading}
      />
      <InternalNetworkReferenceLists networkId={networkId} />
    </HomePageLayout>
  );
}
